import { useState } from "react"
import { useForm } from "@inertiajs/react"
import { Switch } from "@headlessui/react"
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react"
import { ChevronUpDownIcon, XCircleIcon } from "@heroicons/react/20/solid"
import clsx from "clsx"
import useUser from "@/hooks/useUser"
import usePlayers from "@/hooks/usePlayers"
import { captureEvent } from "@/lib/posthog"
import { NestedPlayer } from "@/types/serializers"
import Button from "./Button"
import Spinner from "./Spinner"
import FideLookupModal from "@/modals/FideLookupModal"

type Inputs = {
  name: string
  fide_id: string
  player_id: string
  discoverable: boolean
  notify_on_opponent_game: boolean
}

const ToggleRow = ({
  enabled,
  disabled,
  loading = false,
  onChange,
  label,
  description,
  dimmed = false,
}: {
  enabled: boolean
  disabled: boolean
  loading?: boolean
  onChange: (value: boolean) => void
  label: string
  description?: string
  dimmed?: boolean
}) => (
  <div className={clsx(
    "flex items-start justify-between gap-x-4",
    dimmed && "opacity-50"
  )}>
    <div className="text-sm font-medium text-balance text-gray-700">
      {label}
      {description ? (
        <p className="mt-1 text-xs font-normal text-gray-500">{description}</p>
      ) : null}
    </div>
    <Switch
      checked={enabled}
      disabled={disabled}
      onChange={onChange}
      className={clsx(
        "group focus:ring-chessdrop-600 relative mt-0.5 inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition duration-200 ease-in-out focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed",
        enabled ? "bg-chessdrop-500" : "bg-gray-200"
      )}
    >
      <span className="sr-only">{label}</span>
      <span
        aria-hidden="true"
        className={clsx(
          "pointer-events-none relative inline-flex h-5 w-5 transform items-center justify-center rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
          enabled && "translate-x-5"
        )}
      >
        {loading ? (
          <Spinner size="xxs" className="text-gray-400" />
        ) : enabled ? (
          <svg fill="currentColor" viewBox="0 0 12 12" className="text-chessdrop-600 h-3 w-3">
            <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
          </svg>
        ) : (
          <svg fill="none" viewBox="0 0 12 12" className="h-3 w-3 text-gray-400">
            <path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
    </Switch>
  </div>
)

const EditProfileForm = () => {
  const { user } = useUser()
  const { players } = usePlayers()
  const [showFideLookup, setShowFideLookup] = useState(false)
  const [comboQuery, setComboQuery] = useState("")

  const defaultValues = {
    name: user?.name ?? "",
    fide_id: user?.fide_id ?? user?.unverified_fide_id ?? "",
    player_id: user?.player_id ?? "",
    discoverable: user?.discoverable ?? false,
    notify_on_opponent_game: user?.notify_on_opponent_game ?? false,
  }

  const hasFideId = Boolean(user?.fide_id || user?.unverified_fide_id)

  const { data, isDirty, setData, patch, processing, errors, reset } =
    useForm<Inputs>(defaultValues)

  const selectedPlayer: NestedPlayer | null =
    players.find((p) => p.id === data.player_id) ?? null

  const filteredPlayers = comboQuery === ""
    ? players
    : players.filter((p) =>
        p.name.toLowerCase().includes(comboQuery.toLowerCase())
      )

  // When My Player selection changes, auto-toggle based on whether a player is selected
  const handlePlayerChange = (player: NestedPlayer | null) => {
    const newPlayerId = player?.id ?? ""
    setData((prev) => ({
      ...prev,
      player_id: newPlayerId,
      discoverable: newPlayerId ? true : false,
      notify_on_opponent_game: newPlayerId ? true : false,
    }))
    setComboQuery("")
  }

  const handleClearPlayer = () => {
    setData((prev) => ({
      ...prev,
      player_id: "",
      discoverable: false,
      notify_on_opponent_game: false,
    }))
    setComboQuery("")
  }

  const saveProfile = (e: React.FormEvent) => {
    e.preventDefault()

    // Track My Player changes
    const originalPlayerId = user?.player_id ?? ""
    if (data.player_id !== originalPlayerId) {
      if (data.player_id) {
        captureEvent("my_player_set", { source: "settings" })
      } else {
        captureEvent("my_player_cleared")
      }
    }

    patch("/settings", {
      async: true,
      preserveScroll: true,
      preserveState: true,
    })
  }

  const handleDiscoverableToggle = (value: boolean) => {
    if (value) {
      setData("discoverable", true)
    } else {
      setData((prev) => ({
        ...prev,
        discoverable: false,
        notify_on_opponent_game: false,
      }))
    }
  }

  const handleNotifyToggle = (value: boolean) => {
    setData("notify_on_opponent_game", value)
  }

  const handleCancel = () => {
    reset()
    setComboQuery("")
  }

  return (
    <>
    <form onSubmit={saveProfile}>
      <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
      <div className="mt-3 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow">
        <div className="space-y-5 px-4 pt-5 pb-6 sm:px-6">
          <div>
            <label
              htmlFor="name"
              className="block pb-1.5 text-sm font-medium text-gray-700"
            >
              Name
            </label>
            <div>
              <input
                type="text"
                id="name"
                autoComplete="name"
                required
                className="focus:ring-chessdrop-600 block w-full rounded-xl border-0 p-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
              />
            </div>
          </div>

          <div className="max-w-52">
            <div className="flex flex-1 justify-between gap-x-4">
              <label
                htmlFor="fide_id"
                className="block flex-1 pb-1.5 text-sm font-medium text-gray-700"
              >
                FIDE ID
              </label>
              {!hasFideId ? (
                <button
                  type="button"
                  onClick={() => setShowFideLookup(true)}
                  className="mb-1.5 text-sm text-gray-600 hover:text-gray-900 hover:underline"
                >
                  Look up
                </button>
              ) : null}
            </div>
            <div>
              <input
                type="number"
                inputMode="numeric"
                id="fide_id"
                disabled={hasFideId}
                className="focus:ring-chessdrop-600 hide-spinners block w-full rounded-xl border-0 p-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-100 disabled:shadow-inner"
                value={data.fide_id}
                onChange={(e) => setData("fide_id", e.target.value)}
              />
              {errors?.fide_id && (
                <p className="mt-1.5 text-sm text-red-500">{errors.fide_id}</p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="player-combobox"
              className="block pb-1.5 text-sm font-medium text-gray-700"
            >
              My Player
            </label>
            <Combobox
              value={selectedPlayer}
              onChange={handlePlayerChange}
              onClose={() => setComboQuery("")}
              as="div"
              className="relative"
              nullable
            >
              <div className="relative">
                <ComboboxInput
                  id="player-combobox"
                  className="focus:ring-chessdrop-600 block w-full rounded-xl border-0 p-3 pr-16 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                  displayValue={(player: NestedPlayer | null) => player?.name ?? ""}
                  onChange={(e) => setComboQuery(e.target.value)}
                  placeholder="None"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-x-0.5">
                  {selectedPlayer ? (
                    <button
                      type="button"
                      onClick={handleClearPlayer}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      aria-label="Clear My Player"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  ) : null}
                  <ComboboxButton className="p-1 text-gray-400 hover:text-gray-600">
                    <ChevronUpDownIcon className="h-5 w-5" />
                  </ComboboxButton>
                </div>
              </div>
              <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none text-sm">
                {filteredPlayers.length === 0 ? (
                  <div className="px-4 py-2 text-gray-500">No players found.</div>
                ) : (
                  filteredPlayers.map((player) => (
                    <ComboboxOption
                      key={player.id}
                      value={player}
                      className={({ focus }) =>
                        clsx(
                          "cursor-default select-none px-4 py-2",
                          focus ? "bg-chessdrop-600 text-white" : "text-gray-900"
                        )
                      }
                    >
                      {player.name}
                    </ComboboxOption>
                  ))
                )}
              </ComboboxOptions>
            </Combobox>
            <p className="mt-1.5 text-xs text-gray-500">
              Link yourself to a player for quick selection when saving games and to view your personal stats.
            </p>
          </div>
        </div>

        <div className="space-y-5 border-t border-gray-100 px-4 py-5 sm:px-6">
          <ToggleRow
            enabled={data.discoverable}
            disabled={processing || !data.player_id}
            loading={processing}
            onChange={handleDiscoverableToggle}
            label="Allow other ChessDrop members to find My Player when entering opponents"
            description="Your account remains private — only your player details are shared."
            dimmed={!data.player_id}
          />

          <ToggleRow
            enabled={data.notify_on_opponent_game}
            disabled={processing || !data.player_id || !data.discoverable}
            loading={processing}
            onChange={handleNotifyToggle}
            label="Alert me when someone creates a game with My Player, so that I can import it."
            dimmed={!data.player_id || !data.discoverable}
          />
        </div>

        {isDirty ? (
          <div className="flex items-center justify-end gap-x-3 border-t border-gray-200 px-4 py-4 sm:px-6">
            <Button
              type="button"
              style="secondary"
              onClick={handleCancel}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button type="submit" loading={processing}>
              Save
            </Button>
          </div>
        ) : null}
      </div>
    </form>

    <FideLookupModal
      show={showFideLookup}
      onClose={() => setShowFideLookup(false)}
      onSelect={(fideId) => setData("fide_id", fideId)}
      initialQuery={data.name}
    />
    </>
  )
}

export default EditProfileForm
